/**
 * Streaming an organization's audit and its trail of runs to endpoints of its
 * own — a SIEM, a log pipeline, a Slack channel. The shapes the account area's
 * Streaming page reads and writes, and the one every delivery carries.
 *
 * **What a receiver gets.** A `POST` of one event as JSON (`StreamEvent`), or,
 * for the `slack` format, `{ "text": "…" }` that an incoming webhook posts as a
 * line. Every delivery carries three headers:
 *
 *   x-airlock-delivery    the delivery's id — the same on every retry of it
 *   x-airlock-timestamp   seconds since the epoch when this attempt was signed
 *   x-airlock-signature   `v1=` and the hex HMAC-SHA256, under the endpoint's
 *                         secret, of `<timestamp>.<body>`
 *
 * A receiver recomputes the HMAC over the timestamp and the raw body, compares
 * in constant time, and refuses a timestamp more than five minutes from its own
 * clock: a captured delivery replayed later then fails, because its signature
 * covers the time it was sent. The secret is shown once, when the endpoint is
 * added; the account area can sign with it again but never shows it again.
 *
 * **What an event holds** is exactly what the two trails hold — an audit line
 * (paths, versions and counts, `org_audit.detail`) or a run record
 * (`@airlock/contracts/runs/record`) — and nothing more: a stream is a copy of
 * what the organization's administrators can already read, delivered elsewhere.
 */

/** What an endpoint is sent: the audit's lines, the runs, or both. */
export const STREAM_KINDS = ['audit', 'runs'] as const;
export type StreamKind = (typeof STREAM_KINDS)[number];

/** JSON for a pipeline; `slack` for an incoming webhook, one readable line per event (runs only once ended). */
export const STREAM_FORMATS = ['json', 'slack'] as const;
export type StreamFormat = (typeof STREAM_FORMATS)[number];

export const STREAM_HEADERS = {
  delivery: 'x-airlock-delivery',
  timestamp: 'x-airlock-timestamp',
  signature: 'x-airlock-signature'
} as const;

/** How far a receiver should let a delivery's timestamp be from its own clock. */
export const STREAM_TOLERANCE_SECONDS = 300;
/** Consecutive failed attempts after which an endpoint is switched off and its administrators mailed. */
export const STREAM_DISABLE_AFTER = 10;
/** Attempts a delivery gets before it is given up — the first, then the retries' waits in seconds. */
export const STREAM_RETRY_SECONDS = [60, 300, 1800, 7200, 21600] as const;

export interface StreamEvent {
  /** Stable across retries: the audit line's id, a run's id and copy, a test's own. */
  id: string;
  type: 'audit' | 'run' | 'test';
  org: { id: string; name: string };
  at: string;
  data: unknown;
}

export interface StreamEndpoint {
  id: string;
  /** Where it delivers, as far as the page shows it: the host and the head of the path, never all of it. */
  host: string;
  hint: string;
  format: StreamFormat;
  kinds: StreamKind[];
  enabled: boolean;
  /** Failed attempts in a row since the last delivery that landed. */
  failures: number;
  disabledAt: string | null;
  disabledReason: string | null;
  createdAt: string;
  lastAttemptAt: string | null;
  lastStatus: number | null;
}

export type StreamDeliveryStatus = 'pending' | 'delivered' | 'failed';

export interface StreamDelivery {
  id: string;
  type: StreamEvent['type'];
  eventId: string;
  status: StreamDeliveryStatus;
  attempts: number;
  /** The receiver's HTTP status on the last attempt, or null when it could not be reached. */
  lastStatus: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
  nextAt: string | null;
}

/** `POST /api/orgs/:org/streams`. */
export interface StreamRequest {
  url: string;
  format: StreamFormat;
  kinds: StreamKind[];
}

/** The answer to adding one: the endpoint, and its secret — this once. */
export interface StreamCreated {
  stream: StreamEndpoint;
  secret: string;
}

export interface StreamList {
  streams: StreamEndpoint[];
  /** Whether this deployment can sign deliveries at all (`STREAMS_SIGNING_KEY`). */
  configured: boolean;
  /**
   * Whether the organization may stream — Enterprise, now. False keeps the
   * endpoints it had listed, delivering nothing, with Remove as their one verb.
   */
  allowed: boolean;
}

/**
 * Whether an address may be an endpoint: `https`, a name rather than an IP
 * literal, no credentials, not this machine — and short enough to keep.
 */
export function streamUrlProblem(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 500) return 'An https address, at most 500 characters.';
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'That is not an address.';
  }
  if (url.protocol !== 'https:') return 'Only https addresses are sent to.';
  if (url.username || url.password) return 'An address with a user name or password in it is refused — put a token in the path or use the signature.';
  const host = url.hostname.toLowerCase();
  if (/^[\d.]+$/.test(host) || host.includes(':') || host.startsWith('[')) return 'Use a host name, not an IP address.';
  if (host === 'localhost' || /\.(localhost|local|internal|lan|home|corp)$/.test(host) || !host.includes('.')) {
    return 'That host is not reachable from the internet.';
  }
  return null;
}
